import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Armchair,
  Calendar,
  CalendarPlus,
  CalendarX,
  CheckCircle2,
  Clock3,
  CreditCard,
  Info,
  PackageOpen,
  PhoneCall,
  Receipt,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { clinicDayRange, clinicTimePartsFromInstant, clinicTodayDateString } from "@/lib/clinicTime";
import { formatDoctorName, hasDoctorPrefix } from "@/lib/doctorName";
import ClinicOnboardingSetup from "@/components/onboarding/ClinicOnboardingSetup";

// Every dashboard value below comes from a confirmed database response.
// A missing data contract stays out of the interface instead of implying zero.

const PERIOD_CHIPS = [
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "mtd", label: "This month" },
];

const CUSTOM_PERIOD_PREFIX = "custom:";
const DAY_ONE_ALERT_KEYS = new Set(["unpaid_after_visit", "tx_plan_expiring_soon", "recall_due"]);
const HERO_SPARKLINE_DAYS = 7;
const DASHBOARD_CAPABILITIES = ["sees_money", "does_front_desk", "does_clinical"];
const DASHBOARD_CARDS = {
  onboarding: { permission: "sees_money" },
  kpis: { permission: "sees_money" },
  actionItems: { permission: "sees_money" },
  revenueTrend: { permission: "sees_money" },
  todaySchedule: { permission: "sees_money" },
  trendSchedule: { permission: "sees_money" },
  txPlanPipeline: { permission: "sees_money" },
  frontDeskWorkbench: { permission: "does_front_desk" },
  clinicalSchedule: { permission: "does_clinical" },
  clinicalProduction: { permission: "does_clinical" },
  clinicalFollowUps: { permission: "does_clinical" },
  adminDoctorProduction: { permission: "sees_money" },
};
const EMPTY_CAPABILITIES = {
  sees_money: false,
  does_front_desk: false,
  does_clinical: false,
};
const FRONT_DESK_FLOW = [
  { key: "scheduled", label: "Scheduled" },
  { key: "confirmed", label: "Confirmed" },
  { key: "arrived", label: "Arrived" },
  { key: "waiting", label: "Waiting" },
  { key: "in_chair", label: "In chair" },
  { key: "ready_for_checkout", label: "Checkout" },
  { key: "completed", label: "Done" },
];
const SEVERITY_STYLE = {
  red: {
    Icon: AlertCircle,
    iconBg: "bg-rose-50",
    iconColor: "text-rose-700",
  },
  amber: {
    Icon: AlertTriangle,
    iconBg: "bg-amber-50",
    iconColor: "text-amber-700",
  },
  blue: {
    Icon: Info,
    iconBg: "bg-sky-50",
    iconColor: "text-sky-700",
  },
};

const STATUS_CHIP_STYLE = {
  Scheduled: { label: "Confirmed", className: "bg-slate-100 text-slate-600" },
  Confirmed: { label: "Confirmed", className: "bg-sky-50 text-sky-700" },
  "Checked In": { label: "Checked in", className: "bg-sky-50 text-sky-700" },
  "In Progress": { label: "In chair", className: "bg-teal-50 text-teal-700" },
  Completed: { label: "Completed", className: "bg-emerald-50 text-emerald-700" },
  "No Show": { label: "No show", className: "bg-rose-50 text-rose-700" },
  Cancelled: { label: "Cancelled", className: "bg-slate-100 text-slate-400" },
  Pencil: { label: "Pencil", className: "bg-slate-100 text-slate-600" },
};

const ACTION_ITEM_COPY = {
  unpaid_after_visit: {
    headline: (i) => `${i.count} patient${i.count === 1 ? "" : "s"} left without paying`,
    sublabel: (i, cur) => `${fmtMoney(i.amount_centavos, cur)} uncollected`,
  },
  tx_plan_expiring_soon: {
    headline: (i) => `${i.count} TX plan${i.count === 1 ? "" : "s"} expiring soon`,
    sublabel: (i, cur) => `${fmtMoney(i.amount_centavos, cur)} value expiring soon`,
  },
  recall_due: {
    headline: (i) => `${i.count} patient${i.count === 1 ? "" : "s"} due for recall`,
    sublabel: () => "No future appointment",
  },
};

function fmtMoney(centavos, currency = "PHP") {
  const symbol =
    currency === "PHP" ? "₱" :
    currency === "USD" ? "$" :
    currency ? `${currency} ` : "";
  return `${symbol}${Math.round((Number(centavos) || 0) / 100).toLocaleString("en-PH")}`;
}

function normalizeDashboardCapabilities(rows) {
  const next = { ...EMPTY_CAPABILITIES };
  for (const row of Array.isArray(rows) ? rows : []) {
    if (DASHBOARD_CAPABILITIES.includes(row?.capability_key)) {
      next[row.capability_key] = row.allowed === true;
    }
  }
  return next;
}

function hasDashboardCapability(capabilities, key) {
  return capabilities?.[key] === true;
}

function canLoadDashboardCard(capabilities, cardKey) {
  const required = DASHBOARD_CARDS[cardKey]?.permission;
  return required ? hasDashboardCapability(capabilities, required) : false;
}

function fmtPct(value) {
  if (value === null || value === undefined) return "—";
  return `${Number(value).toFixed(1)}%`;
}

function formatAppointmentTime(value) {
  const parts = clinicTimePartsFromInstant(value);
  if (!parts) return "—";
  const hour12 = parts.hour % 12 || 12;
  const suffix = parts.hour >= 12 ? "PM" : "AM";
  return `${hour12}:${String(parts.minute).padStart(2, "0")} ${suffix}`;
}

function shortDoctorName(name) {
  const trimmed = formatDoctorName(name, "Unassigned");
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length <= 2) return trimmed;
  if (hasDoctorPrefix(trimmed)) return `Dr. ${parts[1]}`;
  return trimmed;
}

function normalizeAppointmentStatus(status) {
  const raw = String(status || "Scheduled").trim();
  const compact = raw.toLowerCase().replace(/[\s_-]+/g, "");
  if (compact === "checkedin") return "Checked In";
  if (compact === "inprogress") return "In Progress";
  if (compact === "noshow") return "No Show";
  if (compact === "cancelled" || compact === "canceled") return "Cancelled";
  if (compact === "completed") return "Completed";
  if (compact === "confirmed") return "Confirmed";
  if (compact === "pencil") return "Pencil";
  return "Scheduled";
}

function appointmentDisplayStatus(row) {
  if (row?.cancelled_at) return "Cancelled";
  return normalizeAppointmentStatus(row?.status);
}

function isAppointmentInChair(row, now = new Date()) {
  const status = appointmentDisplayStatus(row);
  const start = new Date(row.scheduled_start);
  const end = new Date(row.scheduled_end);
  const isDuringSlot = !Number.isNaN(start.getTime())
    && !Number.isNaN(end.getTime())
    && start <= now
    && now < end;
  return status === "In Progress" || (status === "Checked In" && isDuringSlot);
}

function parseCustomPeriod(period) {
  if (!String(period || "").startsWith(CUSTOM_PERIOD_PREFIX)) return null;
  const [, start, end] = String(period).split(":");
  if (!isDateOnly(start) || !isDateOnly(end)) return null;
  return { start, end };
}

function buildCustomPeriod(start, end) {
  if (!isDateOnly(start) || !isDateOnly(end)) return null;
  return `${CUSTOM_PERIOD_PREFIX}${start}:${end}`;
}

function dateRangeForPeriod(period) {
  const custom = parseCustomPeriod(period);
  if (custom) return custom;
  const today = clinicTodayDateString();
  const end = today;
  if (period === "7d") {
    return { start: addDays(new Date(`${today}T00:00:00.000Z`), -6).toISOString().slice(0, 10), end };
  }
  if (period === "mtd") {
    return { start: `${today.slice(0, 8)}01`, end };
  }
  return { start: today, end };
}

function isDateOnly(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));
}

function addDays(date, days) {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

function recentClinicDayKeys(count = HERO_SPARKLINE_DAYS, endDay = clinicTodayDateString()) {
  const end = new Date(`${endDay}T00:00:00.000Z`);
  if (Number.isNaN(end.getTime())) return [];
  return Array.from({ length: count }, (_, index) => {
    const day = addDays(end, index - count + 1);
    return day.toISOString().slice(0, 10);
  });
}

function dayKeyFromInstant(value) {
  const parts = clinicTimePartsFromInstant(value);
  if (!parts) return null;
  return `${parts.year}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
}

function buildAppointmentTrend(rows, metric) {
  const buckets = new Map(
    recentClinicDayKeys().map((day) => [day, { day, value: 0 }])
  );
  for (const row of Array.isArray(rows) ? rows : []) {
    const key = dayKeyFromInstant(row.scheduled_start);
    if (!key) continue;
    if (!buckets.has(key)) buckets.set(key, { day: key, value: 0 });
    const status = appointmentDisplayStatus(row);
    if (metric === "patients" && (status === "Completed" || status === "Checked In" || isAppointmentInChair(row))) {
      buckets.get(key).value += 1;
    }
    if (metric === "scheduled" && status !== "Cancelled") {
      buckets.get(key).value += 1;
    }
    if (metric === "no_show" && status === "No Show") {
      buckets.get(key).value += 1;
    }
  }
  return [...buckets.values()].sort((a, b) => a.day.localeCompare(b.day));
}

function computeRevenueTrend(byDay) {
  const values = (Array.isArray(byDay) ? byDay : [])
    .map((row) => Number(row.amount_centavos || row.value || 0));
  if (values.length < 2) return null;
  const today = values[values.length - 1];
  const yesterday = values[values.length - 2];
  if (yesterday === 0) return null;
  return Math.round(((today - yesterday) / yesterday) * 100);
}

function computeAverageComparison(data) {
  const values = (Array.isArray(data) ? data : []).map((row) => Number(row.value || 0));
  if (values.length < 2) return null;
  const today = values[values.length - 1];
  const prior = values.slice(0, -1);
  const avg = prior.reduce((sum, value) => sum + value, 0) / prior.length;
  if (avg === 0 && today === 0) return "better than avg";
  if (today > avg) return "worse than avg";
  return "better than avg";
}

export default function DashboardV2() {
  const { activeClinic, activeClinicId, user, clinicRole } = useAuth();
  const { activeBranchId, loading: branchLoading } = useBranch();
  const [period, setPeriod] = useState("today");
  const [dashboardCapabilities, setDashboardCapabilities] = useState(null);
  const [frontDeskWorkbench, setFrontDeskWorkbench] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [actionItems, setActionItems] = useState([]);
  const [todaySchedule, setTodaySchedule] = useState([]);
  const [trendSchedule, setTrendSchedule] = useState([]);
  const [byDay, setByDay] = useState([]);
  const [txPlanPipeline, setTxPlanPipeline] = useState({});
  const [clinicalDoctor, setClinicalDoctor] = useState(null);
  const [clinicalSchedule, setClinicalSchedule] = useState([]);
  const [clinicalProduction, setClinicalProduction] = useState([]);
  const [clinicalFollowUps, setClinicalFollowUps] = useState([]);
  const [adminDoctorProduction, setAdminDoctorProduction] = useState([]);
  const [onboardingState, setOnboardingState] = useState(null);
  const [onboardingError, setOnboardingError] = useState(null);
  const [inventoryActivation, setInventoryActivation] = useState(null);
  const [cardErrors, setCardErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!activeBranchId) {
      setLoading(Boolean(branchLoading));
      setError(branchLoading ? null : "No active branch selected.");
      setDashboardCapabilities(null);
      setFrontDeskWorkbench(null);
      setKpis(null);
      setActionItems([]);
      setTodaySchedule([]);
      setTrendSchedule([]);
      setByDay([]);
      setTxPlanPipeline({});
      setClinicalDoctor(null);
      setClinicalSchedule([]);
      setClinicalProduction([]);
      setClinicalFollowUps([]);
      setAdminDoctorProduction([]);
      setInventoryActivation(null);
      return;
    }
    setLoading(true);
    setError(null);
    setOnboardingError(null);
    setCardErrors({});
    const todayRange = clinicDayRange(clinicTodayDateString());
    const trendFrom = addDays(todayRange.from, -6);
    const capabilitiesRes = await supabase.rpc("my_dashboard_capabilities");

    if (capabilitiesRes.error) {
      setError(capabilitiesRes.error.message || "Could not load dashboard access.");
      setLoading(false);
      return;
    }

    const capabilities = normalizeDashboardCapabilities(capabilitiesRes.data);
    const canSeeMoney = hasDashboardCapability(capabilities, "sees_money");
    const canDoFrontDesk = hasDashboardCapability(capabilities, "does_front_desk");
    const canDoClinical = hasDashboardCapability(capabilities, "does_clinical");
    const clinicalOnly = canDoClinical && !canSeeMoney && !canDoFrontDesk;
    setDashboardCapabilities(capabilities);

    async function loadCard(cardKey, request, onSuccess, fallback, label) {
      if (!canLoadDashboardCard(capabilities, cardKey)) {
        onSuccess(fallback);
        return;
      }
      try {
        const res = await request();
        if (res?.error) throw res.error;
        onSuccess(res?.data ?? fallback);
      } catch (cardError) {
        // eslint-disable-next-line no-console
        console.error(`[Dashboard card: ${cardKey}]`, cardError);
        setCardErrors((prev) => ({
          ...prev,
          [cardKey]: cardError?.message || `Could not load ${label || "dashboard card"}.`,
        }));
        onSuccess(fallback);
      }
    }

    const tasks = [];
    if (canLoadDashboardCard(capabilities, "onboarding")) {
      tasks.push((async () => {
        const onboardingRes = await supabase.rpc("get_onboarding_checklist_state");
        if (onboardingRes.error) {
          console.error("[Onboarding RPC]", onboardingRes.error);
          setOnboardingState(null);
          setOnboardingError(onboardingRes.error);
        } else {
          setOnboardingState(onboardingRes.data || null);
          setOnboardingError(null);
        }
      })());
    } else {
      setOnboardingState(null);
      setOnboardingError(null);
    }
    if (clinicRole === "clinic_admin") {
      tasks.push((async () => {
        const inventoryRes = await supabase.rpc("inventory_admin_snapshot", { p_branch_id: activeBranchId });
        if (inventoryRes.error) {
          console.error("[Inventory activation RPC]", inventoryRes.error);
          setInventoryActivation(null);
          return;
        }
        const supplies = Array.isArray(inventoryRes.data?.supplies) ? inventoryRes.data.supplies : [];
        setInventoryActivation({
          empty: supplies.length === 0,
          branchName: inventoryRes.data?.branch_name || "this branch",
        });
      })());
    } else {
      setInventoryActivation(null);
    }
    tasks.push(loadCard(
      "kpis",
      () => supabase.rpc("dashboard_kpis", { p_period: period, p_branch_id: activeBranchId }),
      (data) => setKpis(data || null),
      null,
      "dashboard totals",
    ));
    tasks.push(loadCard(
      "actionItems",
      () => supabase.rpc("dashboard_action_items", { p_period: period, p_branch_id: activeBranchId }),
      (data) => setActionItems(Array.isArray(data) ? data : []),
      [],
      "action items",
    ));
    tasks.push(loadCard(
      "revenueTrend",
      () => supabase.rpc("dashboard_revenue_by_day", { p_period: period === "today" ? "7d" : period, p_branch_id: activeBranchId }),
      (data) => setByDay(Array.isArray(data) ? data : []),
      [],
      "revenue trend",
    ));
    tasks.push(loadCard(
      "todaySchedule",
      () => supabase.rpc("list_appointments", {
        p_from: todayRange.from.toISOString(),
        p_to: todayRange.to.toISOString(),
        p_doctor_id: null,
        p_chair_id: null,
        p_branch_id: activeBranchId,
      }),
      (data) => setTodaySchedule(Array.isArray(data) ? data : []),
      [],
      "today's schedule",
    ));
    tasks.push(loadCard(
      "trendSchedule",
      () => supabase.rpc("list_appointments", {
        p_from: trendFrom.toISOString(),
        p_to: todayRange.to.toISOString(),
        p_doctor_id: null,
        p_chair_id: null,
        p_branch_id: activeBranchId,
      }),
      (data) => setTrendSchedule(Array.isArray(data) ? data : []),
      [],
      "schedule trends",
    ));
    tasks.push(loadCard(
      "txPlanPipeline",
      () => supabase.rpc("dashboard_tx_plan_pipeline_counts", { p_branch_id: activeBranchId }),
      (data) => setTxPlanPipeline(data || {}),
      {},
      "Tx Plan pipeline",
    ));
    tasks.push(loadCard(
      "frontDeskWorkbench",
      () => supabase.rpc("front_desk_workbench", { p_work_date: clinicTodayDateString(), p_branch_id: activeBranchId }),
      (data) => setFrontDeskWorkbench(data || null),
      null,
      "front-desk workbench",
    ));

    if (clinicRole === "clinic_admin" && canSeeMoney) {
      const productionRange = dateRangeForPeriod(period);
      tasks.push(loadCard(
        "adminDoctorProduction",
        () => supabase.rpc("report_doctor_production", {
          p_doctor_id: null,
          p_branch_id: activeBranchId,
          p_start_date: productionRange.start,
          p_end_date: productionRange.end,
          p_include_adjustments: true,
        }),
        (data) => setAdminDoctorProduction(Array.isArray(data) ? data : []),
        [],
        "doctor production",
      ));
    } else {
      setAdminDoctorProduction([]);
    }

    if (!clinicalOnly) {
      setClinicalDoctor(null);
      setClinicalSchedule([]);
      setClinicalProduction([]);
      setClinicalFollowUps([]);
    } else {
      tasks.push((async () => {
        try {
          const doctorsRes = await supabase.rpc("list_clinic_doctor_records", {
            p_branch_id: activeBranchId,
          });
          if (doctorsRes.error) throw doctorsRes.error;
          const doctor = (Array.isArray(doctorsRes.data) ? doctorsRes.data : [])
            .find((row) => row?.profile_id === user?.id);
          if (!doctor?.id) throw new Error("No doctor record is linked to this login.");
          setClinicalDoctor(doctor);
          const productionRange = dateRangeForPeriod(period);
          await Promise.allSettled([
            loadCard(
              "clinicalSchedule",
              () => supabase.rpc("list_appointments", {
                p_from: todayRange.from.toISOString(),
                p_to: todayRange.to.toISOString(),
                p_doctor_id: doctor.id,
                p_chair_id: null,
                p_branch_id: activeBranchId,
              }),
              (data) => setClinicalSchedule(Array.isArray(data) ? data : []),
              [],
              "doctor schedule",
            ),
            loadCard(
              "clinicalProduction",
              () => supabase.rpc("report_doctor_production", {
                p_doctor_id: doctor.id,
                p_branch_id: activeBranchId,
                p_start_date: productionRange.start,
                p_end_date: productionRange.end,
                p_include_adjustments: true,
              }),
              (data) => setClinicalProduction(Array.isArray(data) ? data : []),
              [],
              "doctor production",
            ),
            loadCard(
              "clinicalFollowUps",
              () => supabase.rpc("dashboard_action_items", { p_period: period, p_branch_id: activeBranchId }),
              (data) => setClinicalFollowUps(Array.isArray(data) ? data : []),
              [],
              "doctor follow-ups",
            ),
          ]);
        } catch (clinicalError) {
          console.error("[Dashboard clinical setup]", clinicalError);
          setClinicalDoctor(null);
          setClinicalSchedule([]);
          setClinicalProduction([]);
          setClinicalFollowUps([]);
          setCardErrors((prev) => ({
            ...prev,
            clinicalSchedule: clinicalError?.message || "Could not load clinical dashboard.",
          }));
        }
      })());
    }

    await Promise.allSettled(tasks);
    setLoading(false);
  }, [activeBranchId, branchLoading, clinicRole, period, user?.id]);

  useEffect(() => { load(); }, [load, activeClinicId]);

  if (loading && !dashboardCapabilities) {
    return <div data-testid="dashboard-v2-loading" className="text-sm text-slate-400">Loading dashboard…</div>;
  }
  if (error) {
    return (
      <div data-testid="dashboard-v2-error" className="rounded-lg border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">
        {error}
      </div>
    );
  }
  if (!dashboardCapabilities) return null;

  const canSeeMoney = hasDashboardCapability(dashboardCapabilities, "sees_money");
  const canDoFrontDesk = hasDashboardCapability(dashboardCapabilities, "does_front_desk");
  const canDoClinical = hasDashboardCapability(dashboardCapabilities, "does_clinical");
  const isClinicAdminView = canSeeMoney && (clinicRole === "clinic_admin" || !clinicRole);
  const currency = kpis?.currency || activeClinic?.currency || "PHP";
  const activeActionItems = actionItems
    .filter((item) => DAY_ONE_ALERT_KEYS.has(item?.key))
    .filter((item) => Number(item?.count || 0) > 0);
  const sortedActions = [...activeActionItems].sort(
    (a, b) => (a.severity_rank ?? 9) - (b.severity_rank ?? 9)
  );

  return (
    <div className="space-y-6" data-testid="dashboard-v2-page">
      <Topbar
        clinicName={activeClinic?.clinic_name}
        period={period}
        onPeriodChange={setPeriod}
        kpis={kpis}
      />

      {isClinicAdminView && onboardingError ? (
        <OnboardingSetupError />
      ) : isClinicAdminView ? (
        <ClinicOnboardingSetup
          state={onboardingState}
          clinicName={activeClinic?.clinic_name}
          onRefresh={(nextState) => {
            if (nextState && typeof nextState === "object" && nextState.items) setOnboardingState(nextState);
            else load();
          }}
        />
      ) : null}

      {clinicRole === "clinic_admin" && inventoryActivation?.empty && !onboardingState?.visible ? (
        <InventoryActivationCard branchName={inventoryActivation.branchName} />
      ) : null}

      {canDoFrontDesk && clinicRole !== "clinic_admin" ? (
        cardErrors.frontDeskWorkbench ? (
          <CardLoadError title="Front desk workbench" message="Couldn't load front-desk workbench." testId="dashboard-card-error-frontDeskWorkbench" />
        ) : (
          <FrontDeskWorkbench workbench={frontDeskWorkbench} currency={currency} />
        )
      ) : null}

      {isClinicAdminView && cardErrors.kpis ? (
        <CardLoadError title="Dashboard totals" message="Couldn't load dashboard totals." testId="dashboard-card-error-kpis" />
      ) : isClinicAdminView && kpis ? (
        <AdminDashboard
          kpis={kpis}
          byDay={byDay}
          todaySchedule={todaySchedule}
          trendSchedule={trendSchedule}
          actionItems={sortedActions}
          txPlanPipeline={txPlanPipeline}
          productionRows={adminDoctorProduction}
          currency={currency}
          cardErrors={cardErrors}
        />
      ) : null}

      {canDoClinical && !canSeeMoney && !canDoFrontDesk ? (
        <ClinicalDashboard
          doctor={clinicalDoctor}
          schedule={clinicalSchedule}
          productionRows={clinicalProduction}
          followUps={clinicalFollowUps}
          currency={currency}
          cardErrors={cardErrors}
        />
      ) : null}

      {!canSeeMoney && !canDoFrontDesk && !canDoClinical ? (
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500" data-testid="dashboard-no-blocks">
          No dashboard blocks are enabled for your account yet.
        </div>
      ) : null}
    </div>
  );
}

function CardLoadError({ title, message, testId }) {
  return (
    <section
      data-testid={testId}
      className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
    >
      <p className="font-semibold">{title}</p>
      <p className="mt-0.5">{message || "Couldn't load this card."}</p>
    </section>
  );
}

function AdminDashboard({
  kpis,
  byDay,
  todaySchedule,
  trendSchedule,
  actionItems,
  txPlanPipeline,
  productionRows,
  currency,
  cardErrors,
}) {
  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {cardErrors.revenueTrend ? (
        <CardLoadError title="Collection trend" message="Couldn't load the collection trend." testId="dashboard-card-error-revenueTrend" />
      ) : null}

      <AdminClinicSnapshot
        kpis={kpis}
        byDay={byDay}
        todaySchedule={todaySchedule}
        trendSchedule={trendSchedule}
        currency={currency}
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.65fr)_minmax(19rem,0.75fr)]">
        <main className="space-y-6">
          {cardErrors.actionItems ? (
            <CardLoadError title="Needs attention" message="Couldn't load clinic exceptions." testId="dashboard-card-error-actionItems" />
          ) : (
            <NeedsAttention items={actionItems} currency={currency} />
          )}

          {cardErrors.todaySchedule ? (
            <CardLoadError title="Today's schedule" message="Couldn't load today's schedule." testId="dashboard-card-error-todaySchedule" />
          ) : (
            <TodaysSchedule rows={todaySchedule} />
          )}
        </main>

        <aside className="space-y-5">
          <OperationsPanel kpis={kpis} />

          {cardErrors.txPlanPipeline ? (
            <CardLoadError title="Treatment follow-through" message="Couldn't load treatment plan progress." testId="dashboard-card-error-txPlanPipeline" />
          ) : (
            <AdminGrowthPanel counts={txPlanPipeline} recallCount={kpis.recall_queue_depth} />
          )}

          {cardErrors.adminDoctorProduction ? (
            <CardLoadError title="Doctor production" message="Couldn't load doctor production." testId="dashboard-card-error-adminDoctorProduction" />
          ) : (
            <AdminDoctorProduction rows={productionRows} currency={currency} periodLabel={kpis.period_label} />
          )}
        </aside>
      </div>
    </div>
  );
}

function AdminClinicSnapshot({ kpis, byDay, todaySchedule, trendSchedule, currency }) {
  const bookings = kpis?.bookings_today || {};
  const summary = buildTodaySummary(bookings, todaySchedule);
  const revenueTrend = computeRevenueTrend(byDay);
  const noShowComparison = computeAverageComparison(buildAppointmentTrend(trendSchedule, "no_show"));
  const periodLabel = String(kpis?.period_label || "Today").toLowerCase();

  const metrics = [
    {
      key: "collected",
      label: `Collected ${periodLabel}`,
      value: fmtMoney(kpis?.revenue_total_centavos, currency),
      note: revenueTrend == null ? "Recorded payments" : `${revenueTrend >= 0 ? "Up" : "Down"} ${Math.abs(revenueTrend)}% from the prior day`,
      tone: "text-teal-800",
    },
    {
      key: "ar",
      label: "Unpaid completed work",
      value: fmtMoney(kpis?.ar_outstanding_centavos, currency),
      note: "Open in Accounts Receivable",
      href: "/ar-center",
      tone: Number(kpis?.ar_outstanding_centavos || 0) > 0 ? "text-amber-800" : "text-slate-900",
    },
    {
      key: "completed",
      label: "Completed today",
      value: String(Number(bookings.completed || summary.completed || 0)),
      note: "Recorded completed visits",
      tone: "text-slate-950",
    },
    {
      key: "scheduled",
      label: "Scheduled today",
      value: String(Number(bookings.scheduled || summary.scheduled || 0)),
      note: `${summary.inChair} in chair · ${summary.waiting} waiting`,
      tone: "text-slate-950",
    },
    {
      key: "no-shows",
      label: "No-shows today",
      value: String(Number(bookings.no_show || 0)),
      note: Number(bookings.no_show || 0) === 0
        ? "No missed appointments"
        : noShowComparison === "worse than avg" ? "Above the recent average" : "At or below the recent average",
      tone: Number(bookings.no_show || 0) > 0 ? "text-rose-700" : "text-slate-950",
    },
  ];

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="admin-clinic-snapshot">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-5 py-4">
        <div>
          <h2 className="text-base font-bold text-slate-950">Clinic snapshot</h2>
          <p className="mt-0.5 text-sm text-slate-500">Money, completed work, and patient flow</p>
        </div>
        <Link to="/reports" className="inline-flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 hover:text-slate-950">
          View reports
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <dl className="grid grid-cols-1 gap-px bg-slate-100 sm:grid-cols-2 xl:grid-cols-5">
        {metrics.map((metric) => {
          const wrapperClass = metric.key === "no-shows" ? "sm:col-span-2 xl:col-span-1" : "";
          const content = (
            <div className="min-h-32 bg-white px-5 py-4">
              <dt className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">{metric.label}</dt>
              <dd className={`mt-3 text-2xl font-bold tabular-nums ${metric.tone}`}>{metric.value}</dd>
              <p className="mt-2 text-sm leading-5 text-slate-500">{metric.note}</p>
            </div>
          );
          return metric.href ? (
            <Link key={metric.key} to={metric.href} className={`${wrapperClass} transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-500`}>
              {content}
            </Link>
          ) : <div key={metric.key} className={wrapperClass}>{content}</div>;
        })}
      </dl>
    </section>
  );
}

function AdminGrowthPanel({ counts, recallCount }) {
  const rows = [
    { label: "Proposed", count: Number(counts?.proposed || 0), tone: "bg-sky-300" },
    { label: "Accepted", count: Number(counts?.accepted || 0), tone: "bg-sky-500" },
    { label: "In progress", count: Number(counts?.in_progress || 0), tone: "bg-blue-700" },
    { label: "Completed", count: Number(counts?.completed || 0), tone: "bg-blue-950" },
  ];
  const max = Math.max(...rows.map((row) => row.count), 1);

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4" data-testid="admin-growth-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-bold text-slate-950">Treatment follow-through</h2>
          <p className="mt-1 text-sm text-slate-500">Plans and patients that still need clinic follow-up</p>
        </div>
        <Link to="/tx-plans" className="inline-flex min-h-11 shrink-0 items-center rounded-lg px-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
          Open plans
        </Link>
      </div>
      <ul className="mt-4 space-y-3">
        {rows.map((row) => <PipelineBar key={row.label} {...row} max={max} />)}
      </ul>
      <Link to="/recall-center" className="mt-4 flex min-h-11 items-center justify-between gap-3 border-t border-slate-100 pt-4 text-sm hover:text-slate-950">
        <span className="font-semibold text-slate-700">Patients due for recall</span>
        <span className="font-bold tabular-nums text-slate-950">{Number(recallCount || 0)}</span>
      </Link>
    </section>
  );
}

function AdminDoctorProduction({ rows, currency, periodLabel }) {
  const doctors = summarizeAdminDoctorProduction(rows).slice(0, 5);

  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="admin-doctor-production">
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <div>
          <h2 className="text-sm font-bold text-slate-950">Doctor production</h2>
          <p className="mt-0.5 text-sm text-slate-500">Completed work posted {String(periodLabel || "today").toLowerCase()}</p>
        </div>
        <Link to="/reports" className="inline-flex min-h-11 items-center rounded-lg px-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
          Open report
        </Link>
      </div>
      {doctors.length > 0 ? (
        <ol className="divide-y divide-slate-100">
          {doctors.map((doctor, index) => (
            <li key={doctor.id} className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
              <div className="min-w-0">
                <p className="truncate font-semibold text-slate-900">{index + 1}. {doctor.name}</p>
                <p className="mt-0.5 text-slate-500">{doctor.rowCount} posted item{doctor.rowCount === 1 ? "" : "s"}</p>
              </div>
              <span className="shrink-0 font-bold tabular-nums text-slate-950">{fmtMoney(doctor.productionCentavos, currency)}</span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="px-4 py-4 text-sm text-slate-500">No doctor production posted for this period.</p>
      )}
    </section>
  );
}

function summarizeAdminDoctorProduction(rows) {
  const doctors = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    const id = row?.doctor_id || row?.doctor_name || "unassigned";
    const current = doctors.get(id) || {
      id,
      name: formatDoctorName(row?.doctor_name, "Unassigned doctor"),
      productionCentavos: 0,
      rowCount: 0,
    };
    current.productionCentavos += Number(row?.credit_value_centavos || 0);
    current.rowCount += 1;
    doctors.set(id, current);
  }
  return [...doctors.values()].sort((a, b) => b.productionCentavos - a.productionCentavos);
}

function ClinicalDashboard({ doctor, schedule, productionRows, followUps, currency, cardErrors }) {
  const visibleFollowUps = (Array.isArray(followUps) ? followUps : [])
    .filter((item) => ["tx_plan_expiring_soon", "recall_due"].includes(item?.key))
    .filter((item) => Number(item?.count || 0) > 0);

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(20rem,0.9fr)]" data-testid="clinical-dashboard">
      <div className="space-y-6">
        {cardErrors.clinicalSchedule ? (
          <CardLoadError title="My schedule today" message="Couldn't load your schedule." testId="dashboard-card-error-clinicalSchedule" />
        ) : (
          <ClinicalScheduleCard doctor={doctor} rows={schedule} />
        )}
      </div>
      <aside className="space-y-4">
        {cardErrors.clinicalProduction ? (
          <CardLoadError title="My production" message="Couldn't load your production." testId="dashboard-card-error-clinicalProduction" />
        ) : (
          <ClinicalProductionCard rows={productionRows} currency={currency} />
        )}
        {cardErrors.clinicalFollowUps ? (
          <CardLoadError title="My follow-ups" message="Couldn't load your follow-ups." testId="dashboard-card-error-clinicalFollowUps" />
        ) : (
          <ClinicalFollowUpsCard items={visibleFollowUps} currency={currency} />
        )}
      </aside>
    </div>
  );
}

function ClinicalScheduleCard({ doctor, rows }) {
  const sorted = useMemo(() => [...(Array.isArray(rows) ? rows : [])].sort((a, b) => (
    new Date(a.scheduled_start || 0).getTime() - new Date(b.scheduled_start || 0).getTime()
  )), [rows]);
  const visibleRows = sorted.slice(0, 6);

  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="clinical-schedule-card">
      <div className="border-b border-slate-200 px-4 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">My schedule today</p>
        {doctor?.display_name ? <p className="mt-0.5 text-sm text-slate-500">{formatDoctorName(doctor.display_name)}</p> : null}
      </div>
      {visibleRows.length > 0 ? (
        <ul className="divide-y divide-slate-100">
          {visibleRows.map((row) => (
            <li key={row.id} className="flex items-center justify-between gap-4 px-4 py-4" data-testid={`clinical-schedule-row-${row.id}`}>
              <div className="min-w-0">
                <Link to={`/patients/${row.patient_id}?tab=visits`} className="text-sm font-semibold text-slate-900">
                  {row.patient_name || "Unnamed patient"}
                </Link>
                <p className="mt-0.5 text-sm text-slate-500">{row.procedures || "Procedure"}</p>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-sm font-semibold tabular-nums text-slate-900">{formatAppointmentTime(row.scheduled_start)}</p>
                <StatusChip status={isAppointmentInChair(row) ? "In Progress" : appointmentDisplayStatus(row)} />
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-4 py-4 text-sm text-slate-500">Nothing scheduled for you today.</p>
      )}
    </section>
  );
}

function ClinicalProductionCard({ rows, currency }) {
  const summary = summarizeDoctorProduction(rows);
  return (
    <article className="rounded-lg border border-slate-200 bg-white p-4" data-testid="clinical-production-card">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">My production</p>
      <dl className="mt-4 space-y-3">
        <div>
          <dt className="text-sm text-slate-500">Production credit</dt>
          <dd className="mt-1 text-3xl font-bold tabular-nums text-slate-950">{fmtMoney(summary.productionCentavos, currency)}</dd>
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-3 text-sm">
          <dt className="text-slate-600">Commission snapshot</dt>
          <dd className="font-semibold tabular-nums text-slate-900">{fmtMoney(summary.commissionCentavos, currency)}</dd>
        </div>
        <div className="flex items-center justify-between gap-3 text-sm">
          <dt className="text-slate-600">Posted rows</dt>
          <dd className="font-semibold tabular-nums text-slate-900">{summary.rowCount}</dd>
        </div>
      </dl>
    </article>
  );
}

function ClinicalFollowUpsCard({ items, currency }) {
  return (
    <article className="rounded-lg border border-slate-200 bg-white p-4" data-testid="clinical-follow-ups-card">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">My follow-ups</p>
      {items.length > 0 ? (
        <ul className="mt-4 space-y-3">
          {items.map((item) => (
            <li key={item.key} className="flex items-start justify-between gap-3 text-sm" data-testid={`clinical-follow-up-${item.key}`}>
              <span className="text-slate-700">{clinicalFollowUpLabel(item)}</span>
              <span className="text-right font-semibold tabular-nums text-slate-900">
                {Number(item.amount_centavos || 0) > 0 ? `${fmtMoney(item.amount_centavos, currency)} · ` : ""}
                {Number(item.count || 0)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-4 text-sm text-slate-500">No clinical follow-ups due right now.</p>
      )}
    </article>
  );
}

function clinicalFollowUpLabel(item) {
  const count = Number(item?.count || 0);
  if (item?.key === "tx_plan_expiring_soon") return `${count} TX plan${count === 1 ? "" : "s"}`;
  if (item?.key === "recall_due") return `${count} recall${count === 1 ? "" : "s"}`;
  return item?.key || "Follow-up";
}

function summarizeDoctorProduction(rows) {
  return (Array.isArray(rows) ? rows : []).reduce((summary, row) => {
    const productionCentavos = Number(row?.credit_value_centavos || 0);
    const shareRaw = Number(row?.doctor_share || 0);
    const share = shareRaw > 1 ? shareRaw / 100 : shareRaw;
    return {
      productionCentavos: summary.productionCentavos + productionCentavos,
      commissionCentavos: summary.commissionCentavos + Math.round(productionCentavos * share),
      rowCount: summary.rowCount + 1,
    };
  }, { productionCentavos: 0, commissionCentavos: 0, rowCount: 0 });
}

function OnboardingSetupError() {
  return (
    <section
      data-testid="onboarding-setup-error"
      className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 flex-none" />
        <span>
          <span className="font-semibold">Couldn't load setup checklist.</span> Refresh to try again.
        </span>
      </div>
    </section>
  );
}

function FrontDeskWorkbench({ workbench, currency }) {
  const patientFlow = workbench?.patient_flow || {};
  const collections = workbench?.checkout_collections || {};
  const followUps = workbench?.follow_ups || {};

  return (
    <div className="space-y-6" data-testid="front-desk-workbench">
      <PatientFlowBlock patientFlow={patientFlow} currency={currency} />
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <CheckoutCollectionsBlock collections={collections} currency={currency} />
        <FollowUpsBlock followUps={followUps} currency={currency} />
      </div>
    </div>
  );
}

function PatientFlowBlock({ patientFlow, currency }) {
  const counts = patientFlow?.counts || {};
  const rows = Array.isArray(patientFlow?.rows) ? patientFlow.rows : [];
  const visibleRows = rows.filter((row) => [
    "arrived",
    "waiting",
    "in_chair",
    "ready_for_checkout",
  ].includes(row?.flow_state)).slice(0, 6);

  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="front-desk-patient-flow">
      <div className="border-b border-slate-200 px-4 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Today's patient flow</p>
      </div>
      <div className="flex flex-wrap gap-2 px-4 py-4">
        {FRONT_DESK_FLOW.map((flow) => (
          <span
            key={flow.key}
            data-testid={`front-desk-flow-count-${flow.key}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm text-slate-700"
          >
            <span>{flow.label}</span>
            <span className="font-bold tabular-nums text-slate-950">{Number(counts?.[flow.key] || 0)}</span>
          </span>
        ))}
      </div>
      {visibleRows.length > 0 ? (
        <ul className="divide-y divide-slate-100">
          {visibleRows.map((row) => <PatientFlowRow key={row.appointment_id || row.patient_id} row={row} currency={currency} />)}
        </ul>
      ) : (
        <p className="px-4 pb-4 text-sm text-slate-500" data-testid="front-desk-patient-flow-empty">
          No active patient handoffs right now.
        </p>
      )}
    </section>
  );
}

function PatientFlowRow({ row, currency }) {
  const state = formatFlowState(row?.flow_state);
  const checkoutHref = row?.checkout_url || (row?.appointment_id ? `/checkout?appointment_id=${row.appointment_id}` : null);
  const isReady = row?.flow_state === "ready_for_checkout" && checkoutHref;

  return (
    <li className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`front-desk-flow-row-${row?.appointment_id || row?.patient_id}`}>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900">
          {row?.patient_name || "Unnamed patient"}
          <span className="font-normal text-slate-500"> · {state}</span>
          {row?.doctor_name ? <span className="font-normal text-slate-500"> · {shortDoctorName(row.doctor_name)}</span> : null}
        </p>
        <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
          {row?.scheduled_start ? (
            <span className="inline-flex items-center gap-1">
              <Clock3 className="h-3.5 w-3.5" />
              {formatAppointmentTime(row.scheduled_start)}
            </span>
          ) : null}
          {Number(row?.amount_centavos || 0) > 0 ? <span>{fmtMoney(row.amount_centavos, currency)} due</span> : null}
        </p>
      </div>
      {isReady ? (
        <Link
          to={checkoutHref}
          data-testid={`front-desk-open-checkout-${row.appointment_id}`}
          className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-50"
        >
          <Receipt className="h-4 w-4" />
          Open checkout
        </Link>
      ) : (
        <span className="text-sm font-semibold text-slate-500">{row?.flow_state === "in_chair" ? "In progress" : state}</span>
      )}
    </li>
  );
}

function CheckoutCollectionsBlock({ collections, currency }) {
  const rows = [
    ...normalizeCollectionRows(collections?.ready_for_checkout, "ready"),
    ...normalizeCollectionRows(collections?.payment_pending, "payment"),
    ...normalizeCollectionRows(collections?.left_unpaid, "ar"),
  ].slice(0, 6);

  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="front-desk-checkout-collections">
      <div className="border-b border-slate-200 px-4 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Checkout & collections</p>
      </div>
      {rows.length > 0 ? (
        <ul className="divide-y divide-slate-100">
          {rows.map((row) => <CollectionRow key={row.key} row={row} currency={currency} />)}
        </ul>
      ) : (
        <p className="px-4 py-4 text-sm text-slate-500">No checkout or collection items right now.</p>
      )}
    </section>
  );
}

function CollectionRow({ row, currency }) {
  const href = row.checkout_url || (row.appointment_id ? `/checkout?appointment_id=${row.appointment_id}` : null);
  const actionLabel = row.kind === "ready" ? "Checkout" : row.kind === "payment" ? "Collect" : "Review";
  const content = (
    <div className="flex items-center justify-between gap-4 px-4 py-4 transition-colors hover:bg-slate-50">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900">
          {row.patient_name || "Unnamed patient"}
          <span className="font-normal text-slate-500"> · {fmtMoney(row.amount_centavos, currency)}</span>
        </p>
        <p className="mt-0.5 text-sm text-slate-500">{collectionKindLabel(row.kind)}</p>
      </div>
      <span className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-800">
        <CreditCard className="h-4 w-4" />
        {actionLabel}
      </span>
    </div>
  );

  return (
    <li data-testid={`front-desk-collection-row-${row.key}`}>
      {href ? <Link to={href}>{content}</Link> : content}
    </li>
  );
}

function FollowUpsBlock({ followUps, currency }) {
  const rows = [
    ...normalizeFollowUpRows(followUps?.recall_due, "recall"),
    ...normalizeFollowUpRows(followUps?.accepted_not_booked, "accepted"),
  ].slice(0, 6);

  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="front-desk-follow-ups">
      <div className="border-b border-slate-200 px-4 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Follow-ups</p>
      </div>
      {rows.length > 0 ? (
        <ul className="divide-y divide-slate-100">
          {rows.map((row) => <FollowUpRow key={row.key} row={row} currency={currency} />)}
        </ul>
      ) : (
        <p className="px-4 py-4 text-sm text-slate-500">No follow-ups due right now.</p>
      )}
    </section>
  );
}

function FollowUpRow({ row, currency }) {
  const label = row.kind === "recall" ? "Recall due" : "Accepted, not booked";
  const actionLabel = row.kind === "recall" ? "Call" : "Book";
  const href = row.drill_to || (row.patient_id ? `/patients/${row.patient_id}?tab=visits` : null);
  const amount = Number(row.amount_centavos || row.value_centavos || 0);
  const content = (
    <div className="flex items-center justify-between gap-4 px-4 py-4 transition-colors hover:bg-slate-50">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900">
          {label}
          <span className="font-normal text-slate-500"> · {row.patient_name || "Unnamed patient"}</span>
        </p>
        {amount > 0 ? <p className="mt-0.5 text-sm text-slate-500">{fmtMoney(amount, currency)}</p> : null}
      </div>
      <span className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-800">
        {row.kind === "recall" ? <PhoneCall className="h-4 w-4" /> : <CalendarPlus className="h-4 w-4" />}
        {actionLabel}
      </span>
    </div>
  );

  return (
    <li data-testid={`front-desk-follow-up-row-${row.key}`}>
      {href ? <Link to={href}>{content}</Link> : content}
    </li>
  );
}

function normalizeCollectionRows(rows, kind) {
  return (Array.isArray(rows) ? rows : []).map((row, index) => ({
    ...row,
    kind,
    key: row.appointment_id || row.payment_link_id || row.ar_receivable_id || `${kind}-${index}`,
  }));
}

function normalizeFollowUpRows(rows, kind) {
  return (Array.isArray(rows) ? rows : []).map((row, index) => ({
    ...row,
    kind,
    key: row.recall_queue_id || row.treatment_plan_id || `${kind}-${index}`,
  }));
}

function formatFlowState(state) {
  const labels = {
    scheduled: "Scheduled",
    confirmed: "Confirmed",
    arrived: "Arrived",
    waiting: "Waiting",
    in_chair: "In chair",
    ready_for_checkout: "Ready for checkout",
    completed: "Done",
    no_show: "No-show",
  };
  return labels[state] || "Scheduled";
}

function collectionKindLabel(kind) {
  if (kind === "ready") return "Ready for checkout";
  if (kind === "payment") return "Payment pending";
  return "Left unpaid";
}

export function InventoryActivationCard({ branchName }) {
  return (
    <section className="flex flex-col gap-5 rounded-xl border border-slate-200 bg-white px-5 py-5 shadow-sm sm:flex-row sm:items-center sm:justify-between" data-testid="inventory-activation-card">
      <div className="flex min-w-0 gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-800">
          <PackageOpen className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.08em] text-sky-700">Optional setup</p>
          <h2 className="mt-1 text-lg font-bold text-slate-950">Start inventory tracking</h2>
          <p className="mt-1 text-sm leading-6 text-slate-600">Track one supply at {branchName} in about five minutes. You do not need to enter the whole stockroom.</p>
        </div>
      </div>
      <Link to="/inventory?setup=first" className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-lg bg-slate-950 px-5 text-sm font-semibold text-white hover:bg-slate-800">
        Track first supply
      </Link>
    </section>
  );
}

function Topbar({ clinicName, period, onPeriodChange, kpis }) {
  const greeting = `Here's what's happening at ${clinicName || "your clinic"} today.`;
  const selectedCustom = parseCustomPeriod(period);
  const today = clinicTodayDateString();
  const [customOpen, setCustomOpen] = useState(Boolean(selectedCustom));
  const [customStart, setCustomStart] = useState(selectedCustom?.start || today);
  const [customEnd, setCustomEnd] = useState(selectedCustom?.end || today);
  const customPeriod = buildCustomPeriod(customStart, customEnd);
  const customInvalid = !customPeriod || customStart > customEnd;

  useEffect(() => {
    const parsed = parseCustomPeriod(period);
    if (!parsed) return;
    setCustomOpen(true);
    setCustomStart(parsed.start);
    setCustomEnd(parsed.end);
  }, [period]);

  function choosePreset(value) {
    setCustomOpen(false);
    onPeriodChange(value);
  }

  function applyCustomRange() {
    if (customInvalid) return;
    onPeriodChange(customPeriod);
  }

  return (
    <header className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">{greeting}</h1>
        <p className="mt-0.5 text-sm text-slate-500">{kpis?.period_label}</p>
      </div>
      <div className="flex flex-col items-end gap-2">
        <div className="flex items-center gap-1.5" role="tablist" aria-label="Period">
          {PERIOD_CHIPS.map((chip) => {
            const active = chip.value === period;
            return (
              <button
                key={chip.value}
                role="tab"
                aria-selected={active}
                onClick={() => choosePreset(chip.value)}
                data-testid={`period-chip-${chip.value}`}
                className={[
                  "rounded-lg border px-3 py-1.5 text-sm transition-colors",
                  active
                    ? "border-slate-900 bg-slate-900 font-semibold text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                ].join(" ")}
              >
                {chip.label}
              </button>
            );
          })}
          <button
            type="button"
            role="tab"
            aria-selected={Boolean(selectedCustom)}
            onClick={() => setCustomOpen(true)}
            data-testid="period-chip-custom"
            className={[
              "rounded-lg border px-3 py-1.5 text-sm transition-colors",
              selectedCustom
                ? "border-slate-900 bg-slate-900 font-semibold text-white"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
            ].join(" ")}
          >
            Custom
          </button>
        </div>
        {customOpen ? (
          <div
            data-testid="dashboard-custom-range"
            className="flex flex-wrap items-end justify-end gap-2 rounded-lg border border-slate-200 bg-white p-2 shadow-sm"
          >
            <DateInput label="From" value={customStart} max={customEnd || undefined} onChange={setCustomStart} testId="dashboard-custom-start" />
            <DateInput label="To" value={customEnd} min={customStart || undefined} onChange={setCustomEnd} testId="dashboard-custom-end" />
            <button
              type="button"
              onClick={applyCustomRange}
              disabled={customInvalid}
              data-testid="dashboard-custom-apply"
              className="h-9 rounded-lg border border-teal-700 bg-teal-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
            >
              Apply
            </button>
          </div>
        ) : null}
      </div>
    </header>
  );
}

function DateInput({ label, value, min, max, onChange, testId }) {
  return (
    <label className="grid gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
      {label}
      <input
        type="date"
        value={value}
        min={min}
        max={max}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testId}
        className="h-9 rounded-lg border border-slate-200 bg-slate-50 px-2 text-sm font-medium normal-case tracking-normal text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
      />
    </label>
  );
}

function buildTodaySummary(bookingsToday, scheduleRows) {
  const rows = Array.isArray(scheduleRows) ? scheduleRows : [];
  const openRows = rows.filter((row) => {
    const status = appointmentDisplayStatus(row);
    return status !== "Cancelled" && status !== "No Show";
  });
  const checkedIn = rows.filter((row) => appointmentDisplayStatus(row) === "Checked In").length;
  // Approximation until in-chair is stored or returned by RPC.
  const inChair = rows.filter(isAppointmentInChair).length;
  const completed = Number(bookingsToday?.completed || 0);
  const scheduled = Number(bookingsToday?.scheduled || 0) || openRows.length || Math.max(
    0,
    Number(bookingsToday?.count || 0)
      - Number(bookingsToday?.no_show || 0)
      - Number(bookingsToday?.cancelled || 0),
  );
  const waiting = rows.filter((row) => {
    const status = appointmentDisplayStatus(row);
    return (status === "Scheduled" || status === "Confirmed" || status === "Pencil") && !isAppointmentInChair(row);
  }).length;

  return {
    scheduled,
    checkedIn: checkedIn || Number(bookingsToday?.checked_in || 0),
    inChair,
    waiting,
    completed,
  };
}

function TodaysSchedule({ rows }) {
  const sorted = useMemo(() => [...(Array.isArray(rows) ? rows : [])].sort((a, b) => (
    new Date(a.scheduled_start || 0).getTime() - new Date(b.scheduled_start || 0).getTime()
  )), [rows]);
  const visibleRows = sorted.slice(0, 8);

  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="dashboard-today-schedule">
      <div className="border-b border-slate-200 px-4 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Today's schedule</p>
      </div>
      {visibleRows.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
            <thead className="bg-white text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
              <tr>
                <th scope="col" className="px-4 py-3">Patient</th>
                <th scope="col" className="px-4 py-3">Time</th>
                <th scope="col" className="px-4 py-3">Procedure</th>
                <th scope="col" className="px-4 py-3">Doctor</th>
                <th scope="col" className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {visibleRows.map((row) => <ScheduleRow key={row.id} row={row} />)}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="px-4 py-4 text-sm text-slate-500" data-testid="dashboard-schedule-empty">
          Nothing scheduled today
        </p>
      )}
      {sorted.length > 8 ? (
        <div className="border-t border-slate-100 px-4 py-3">
          <Link
            to="/calendar"
            className="text-sm font-semibold text-slate-700 hover:text-slate-950"
            data-testid="dashboard-schedule-full-link"
          >
            View full schedule →
          </Link>
        </div>
      ) : null}
    </section>
  );
}

function ScheduleRow({ row }) {
  const status = isAppointmentInChair(row) ? "In Progress" : appointmentDisplayStatus(row);
  const isCancelled = status === "Cancelled";
  return (
    <tr data-testid={`dashboard-schedule-row-${row.id}`} className="hover:bg-slate-50">
      <td className="px-4 py-3">
        <Link
          to={`/patients/${row.patient_id}?tab=visits`}
          className={`font-semibold ${isCancelled ? "text-slate-400 line-through" : "text-slate-900"}`}
        >
          {row.patient_name || "Unnamed patient"}
        </Link>
      </td>
      <td className="px-4 py-3 tabular-nums text-slate-600">{formatAppointmentTime(row.scheduled_start)}</td>
      <td className="px-4 py-3 text-slate-700">{row.procedures || "Procedure"}</td>
      <td className="px-4 py-3 text-slate-600">{shortDoctorName(row.doctor_name)}</td>
      <td className="px-4 py-3"><StatusChip status={status} /></td>
    </tr>
  );
}

function StatusChip({ status }) {
  const config = STATUS_CHIP_STYLE[status] || STATUS_CHIP_STYLE.Scheduled;
  return (
    <span className={`inline-flex rounded-md px-2 py-1 text-xs font-semibold ${config.className}`}>
      {config.label}
    </span>
  );
}

function NeedsAttention({ items, currency }) {
  return (
    <section data-testid="action-items" className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="border-b border-slate-200 px-4 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Needs attention</p>
      </div>
      {Array.isArray(items) && items.length > 0 ? (
        <ul className="divide-y divide-slate-100">
          {items.map((item) => <ActionItemRow key={item.key} item={item} currency={currency} />)}
        </ul>
      ) : (
        <p className="px-4 py-4 text-sm text-slate-500">No urgent clinic items right now.</p>
      )}
    </section>
  );
}

function ActionItemRow({ item, currency }) {
  const style = SEVERITY_STYLE[item.severity] || SEVERITY_STYLE.blue;
  const copy = ACTION_ITEM_COPY[item.key] || {
    headline: () => item.key,
    sublabel: () => "",
  };
  const headline = copy.headline(item);
  const sublabel = copy.sublabel(item, currency);
  const Icon = style.Icon;

  const row = (
    <div className="flex items-center gap-4 px-4 py-4 transition-colors hover:bg-slate-50">
      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${style.iconBg}`}>
        <Icon className={`h-5 w-5 ${style.iconColor}`} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-900">{headline}</p>
        {sublabel ? <p className="mt-0.5 text-sm text-slate-500">{sublabel}</p> : null}
      </div>
      {item.drill_to ? (
        <span className="inline-flex min-h-11 shrink-0 items-center gap-1 rounded-lg px-2 text-sm font-semibold text-slate-700">
          Review
          <ArrowRight className="h-4 w-4" />
        </span>
      ) : null}
    </div>
  );

  return (
    <li data-testid={`action-item-${item.key}`} data-severity={item.severity}>
      {item.drill_to ? <Link to={item.drill_to}>{row}</Link> : row}
    </li>
  );
}

function PipelineBar({ label, count, max, tone }) {
  const pct = max > 0 ? (count / max) * 100 : 0;
  return (
    <li className="grid grid-cols-[6.5rem_minmax(0,1fr)_2rem] items-center gap-3 text-sm">
      <span className="text-slate-700">{label}</span>
      <span className="h-2 overflow-hidden rounded-full bg-slate-100">
        <span className={`block h-full rounded-full ${tone}`} style={{ width: `${pct}%` }} />
      </span>
      <span className="text-right font-semibold tabular-nums text-slate-900">{count}</span>
    </li>
  );
}

function OperationsPanel({ kpis }) {
  const visits = kpis.visits || {};
  const chair = kpis.chair_utilization || {};
  return (
    <article className="rounded-lg border border-slate-200 bg-white p-4" data-testid="ops-panel">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Operations</p>
      <dl className="mt-4 space-y-3">
        <OpRow Icon={CheckCircle2} label="Visits completed" value={String(visits.completed || 0)} />
        <OpRow Icon={Calendar} label="Scheduled" value={String(visits.scheduled || 0)} />
        <OpRow Icon={CalendarX} label="No-shows" value={String(visits.no_show || 0)} tone={visits.no_show > 0 ? "rose" : "slate"} />
        <OpRow
          Icon={Armchair}
          label="Chair utilization"
          value={`${fmtPct(chair.pct)}${chair.overbooked ? " !" : ""}`}
        />
      </dl>
    </article>
  );
}

function OpRow({ Icon, label, value, tone = "slate" }) {
  const tones = {
    slate: "text-slate-900",
    rose: "text-rose-700",
  };
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <div className="flex min-w-0 items-center gap-2">
        <Icon className="h-4 w-4 shrink-0 text-slate-400" />
        <dt className="text-slate-600">{label}</dt>
      </div>
      <dd className={`font-semibold tabular-nums ${tones[tone] || tones.slate}`}>{value}</dd>
    </div>
  );
}
